/**
 * CMSPageBuilder - Admin component for block-based page editing
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { cmsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MediaUpload } from '@/components/MediaUpload';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  ArrowLeft,
  Save,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Settings,
  LayoutTemplate,
  Type,
  Image as ImageIcon,
  Video,
  LayoutGrid,
  MousePointerClick,
  Minus,
  Expand,
  Columns,
  ChevronsDown,
  Quote,
  Phone,
  Share2,
  Code,
  Church,
  FileText,
  Globe
} from 'lucide-react';

// Block type to icon mapping
const BLOCK_ICONS = {
  hero: LayoutTemplate,
  text: Type,
  image: ImageIcon,
  image_gallery: LayoutGrid,
  video: Video,
  cards: LayoutGrid,
  cta: MousePointerClick,
  divider: Minus,
  spacer: Expand,
  two_columns: Columns,
  accordion: ChevronsDown,
  testimonials: Quote,
  contact_info: Phone,
  social_links: Share2,
  embed: Code,
  churches_list: Church
};

// ============== CMS Pages List ==============
export const CMSPagesList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [newPageData, setNewPageData] = useState({ title: '', slug: '' });

  const { data: pages, isLoading } = useQuery({
    queryKey: ['cms-pages'],
    queryFn: () => cmsAPI.adminListPages().then(r => r.data)
  });

  const { data: templates } = useQuery({
    queryKey: ['cms-templates'],
    queryFn: () => cmsAPI.getTemplates().then(r => r.data)
  });

  const createMutation = useMutation({
    mutationFn: (data) => cmsAPI.createPage(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['cms-pages']);
      toast.success('Page created successfully');
      setShowCreateDialog(false);
      navigate(`/admin/cms/pages/${response.data.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create page');
    }
  });

  const createFromTemplateMutation = useMutation({
    mutationFn: ({ templateId, title, slug }) => cmsAPI.createFromTemplate(templateId, title, slug),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['cms-pages']);
      toast.success('Page created from template');
      setShowTemplateDialog(false);
      navigate(`/admin/cms/pages/${response.data.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create page');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (pageId) => cmsAPI.deletePage(pageId),
    onSuccess: () => {
      queryClient.invalidateQueries(['cms-pages']);
      toast.success('Page deleted');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete page');
    }
  });

  const duplicateMutation = useMutation({
    mutationFn: (pageId) => cmsAPI.duplicatePage(pageId),
    onSuccess: () => {
      queryClient.invalidateQueries(['cms-pages']);
      toast.success('Page duplicated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to duplicate page');
    }
  });

  const handleCreate = () => {
    if (!newPageData.title || !newPageData.slug) {
      toast.error('Title and slug are required');
      return;
    }
    createMutation.mutate({
      title: newPageData.title,
      slug: newPageData.slug,
      blocks: [],
      is_published: false
    });
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">CMS Pages</h2>
          <p className="text-sm text-slate-500 mt-1">
            Create and manage custom pages with the block-based editor
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTemplateDialog(true)}>
            <LayoutTemplate className="h-4 w-4 mr-2" />
            From Template
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-page-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Page
          </Button>
        </div>
      </div>

      {pages?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No pages created yet</p>
            <p className="text-sm text-slate-500 mt-1">Create your first page to get started</p>
            <div className="flex justify-center gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowTemplateDialog(true)}>
                Start from Template
              </Button>
              <Button onClick={() => setShowCreateDialog(true)}>
                Create Blank Page
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages?.map(page => (
            <Card key={page.id} className="group hover:shadow-md transition-shadow" data-testid={`page-card-${page.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">{page.title}</CardTitle>
                    <CardDescription className="mt-1">
                      <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                        /{page.slug}
                      </span>
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/cms/pages/${page.id}`)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => window.open(`/page/${page.slug}`, '_blank')}>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Preview
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicateMutation.mutate(page.id)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => {
                          if (window.confirm('Delete this page?')) {
                            deleteMutation.mutate(page.id);
                          }
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {page.is_published ? (
                      <Badge className="bg-green-100 text-green-800">
                        <Globe className="h-3 w-3 mr-1" />
                        Published
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <EyeOff className="h-3 w-3 mr-1" />
                        Draft
                      </Badge>
                    )}
                    <span className="text-xs text-slate-500">
                      {page.block_count} blocks
                    </span>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => navigate(`/admin/cms/pages/${page.id}`)}
                  >
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Blank Page Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Page</DialogTitle>
            <DialogDescription>
              Create a blank page and add blocks using the editor
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Page Title</Label>
              <Input
                id="title"
                value={newPageData.title}
                onChange={(e) => {
                  setNewPageData({
                    title: e.target.value,
                    slug: generateSlug(e.target.value)
                  });
                }}
                placeholder="About Us"
                data-testid="page-title-input"
              />
            </div>
            <div>
              <Label htmlFor="slug">URL Slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">/page/</span>
                <Input
                  id="slug"
                  value={newPageData.slug}
                  onChange={(e) => setNewPageData({ ...newPageData, slug: e.target.value })}
                  placeholder="about-us"
                  data-testid="page-slug-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Page'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Choose a Template</DialogTitle>
            <DialogDescription>
              Start with a pre-built template and customize it
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto py-4">
            {templates?.map(template => (
              <Card 
                key={template.id}
                className="cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                onClick={() => {
                  const title = prompt('Enter page title:', template.name);
                  if (title) {
                    createFromTemplateMutation.mutate({
                      templateId: template.id,
                      title,
                      slug: generateSlug(title)
                    });
                  }
                }}
              >
                <CardHeader className="pb-2">
                  <div className="h-24 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center mb-2">
                    <LayoutTemplate className="h-8 w-8 text-slate-400" />
                  </div>
                  <CardTitle className="text-sm">{template.name}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-slate-500">{template.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== CMS Page Editor ==============
export const CMSPageEditor = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [pageData, setPageData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState(null);
  const [showBlockPicker, setShowBlockPicker] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: blockTypes } = useQuery({
    queryKey: ['block-types'],
    queryFn: () => cmsAPI.getBlockTypes().then(r => r.data)
  });

  const { data: page, isLoading } = useQuery({
    queryKey: ['cms-page', pageId],
    queryFn: () => cmsAPI.adminGetPage(pageId).then(r => r.data),
    enabled: !!pageId
  });

  useEffect(() => {
    if (page) {
      setPageData(page);
      setBlocks(page.blocks || []);
    }
  }, [page]);

  const saveMutation = useMutation({
    mutationFn: (data) => cmsAPI.updatePage(pageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cms-page', pageId]);
      queryClient.invalidateQueries(['cms-pages']);
      toast.success('Page saved');
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to save page');
    }
  });

  const handleSave = () => {
    saveMutation.mutate({
      ...pageData,
      blocks
    });
  };

  const addBlock = (blockType) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type: blockType.type,
      content: { ...blockType.default_content }
    };
    setBlocks([...blocks, newBlock]);
    setShowBlockPicker(false);
    setHasChanges(true);
    setSelectedBlockIndex(blocks.length);
  };

  const updateBlock = (index, content) => {
    const updated = [...blocks];
    updated[index] = { ...updated[index], content };
    setBlocks(updated);
    setHasChanges(true);
  };

  const deleteBlock = (index) => {
    const updated = blocks.filter((_, i) => i !== index);
    setBlocks(updated);
    setSelectedBlockIndex(null);
    setHasChanges(true);
  };

  const moveBlock = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    
    const updated = [...blocks];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setBlocks(updated);
    setSelectedBlockIndex(newIndex);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!pageData) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Editor Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/cms')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold">{pageData.title}</h1>
              <span className="text-xs text-slate-500 font-mono">/page/{pageData.slug}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                Unsaved changes
              </Badge>
            )}
            <div className="flex items-center gap-2 border-l pl-3">
              <Label htmlFor="published" className="text-sm">Published</Label>
              <Switch
                id="published"
                checked={pageData.is_published}
                onCheckedChange={(checked) => {
                  setPageData({ ...pageData, is_published: checked });
                  setHasChanges(true);
                }}
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => window.open(`/page/${pageData.slug}`, '_blank')}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending || !hasChanges}>
              <Save className="h-4 w-4 mr-2" />
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Blocks Canvas */}
          <div className="col-span-8">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Page Blocks</CardTitle>
                  <Button size="sm" onClick={() => setShowBlockPicker(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Block
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {blocks.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed rounded-lg">
                    <LayoutGrid className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No blocks yet</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => setShowBlockPicker(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add your first block
                    </Button>
                  </div>
                ) : (
                  blocks.map((block, index) => {
                    const Icon = BLOCK_ICONS[block.type] || LayoutTemplate;
                    const isSelected = selectedBlockIndex === index;
                    
                    return (
                      <div
                        key={block.id || index}
                        className={`group border rounded-lg p-4 cursor-pointer transition-all ${
                          isSelected ? 'border-blue-500 bg-blue-50' : 'hover:border-slate-300'
                        }`}
                        onClick={() => setSelectedBlockIndex(index)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded">
                              <Icon className="h-4 w-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium capitalize">{block.type.replace('_', ' ')}</p>
                              <p className="text-xs text-slate-500">
                                {block.content?.title || block.content?.heading || block.content?.content?.substring(0, 50) || 'Click to edit'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); moveBlock(index, 'up'); }}
                              disabled={index === 0}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); moveBlock(index, 'down'); }}
                              disabled={index === blocks.length - 1}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={(e) => { e.stopPropagation(); deleteBlock(index); }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>

          {/* Block Editor Panel */}
          <div className="col-span-4">
            <Card className="sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">
                  {selectedBlockIndex !== null ? 'Edit Block' : 'Page Settings'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedBlockIndex !== null && blocks[selectedBlockIndex] ? (
                  <BlockEditor
                    block={blocks[selectedBlockIndex]}
                    onChange={(content) => updateBlock(selectedBlockIndex, content)}
                  />
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label>Page Title</Label>
                      <Input
                        value={pageData.title}
                        onChange={(e) => {
                          setPageData({ ...pageData, title: e.target.value });
                          setHasChanges(true);
                        }}
                      />
                    </div>
                    <div>
                      <Label>URL Slug</Label>
                      <Input
                        value={pageData.slug}
                        onChange={(e) => {
                          setPageData({ ...pageData, slug: e.target.value });
                          setHasChanges(true);
                        }}
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={pageData.description || ''}
                        onChange={(e) => {
                          setPageData({ ...pageData, description: e.target.value });
                          setHasChanges(true);
                        }}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Meta Title (SEO)</Label>
                      <Input
                        value={pageData.meta_title || ''}
                        onChange={(e) => {
                          setPageData({ ...pageData, meta_title: e.target.value });
                          setHasChanges(true);
                        }}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Block Picker Dialog */}
      <Dialog open={showBlockPicker} onOpenChange={setShowBlockPicker}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add a Block</DialogTitle>
            <DialogDescription>Choose a block type to add to your page</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto py-4">
            {blockTypes?.map(blockType => {
              const Icon = BLOCK_ICONS[blockType.type] || LayoutTemplate;
              return (
                <Card
                  key={blockType.type}
                  className="cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                  onClick={() => addBlock(blockType)}
                >
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Icon className="h-6 w-6 text-slate-600" />
                    </div>
                    <p className="font-medium text-sm">{blockType.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{blockType.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== Block Editor Component ==============
const BlockEditor = ({ block, onChange }) => {
  const content = block.content || {};

  const updateField = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  // Render different editors based on block type
  switch (block.type) {
    case 'hero':
      return (
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={content.subtitle || ''}
              onChange={(e) => updateField('subtitle', e.target.value)}
            />
          </div>
          <div>
            <Label>CTA Button Text</Label>
            <Input
              value={content.cta_text || ''}
              onChange={(e) => updateField('cta_text', e.target.value)}
            />
          </div>
          <div>
            <Label>CTA Link</Label>
            <Input
              value={content.cta_link || ''}
              onChange={(e) => updateField('cta_link', e.target.value)}
            />
          </div>
          <div>
            <Label>Background Type</Label>
            <Select
              value={content.background_type || 'gradient'}
              onValueChange={(val) => updateField('background_type', val)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gradient">Gradient</SelectItem>
                <SelectItem value="color">Solid Color</SelectItem>
                <SelectItem value="image">Image</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {content.background_type === 'gradient' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>From Color</Label>
                <Input
                  type="color"
                  value={content.gradient_from || '#3B82F6'}
                  onChange={(e) => updateField('gradient_from', e.target.value)}
                />
              </div>
              <div>
                <Label>To Color</Label>
                <Input
                  type="color"
                  value={content.gradient_to || '#6366F1'}
                  onChange={(e) => updateField('gradient_to', e.target.value)}
                />
              </div>
            </div>
          )}
          {content.background_type === 'color' && (
            <div>
              <Label>Background Color</Label>
              <Input
                type="color"
                value={content.background_color || '#3B82F6'}
                onChange={(e) => updateField('background_color', e.target.value)}
              />
            </div>
          )}
          <div>
            <Label>Text Color</Label>
            <Input
              type="color"
              value={content.text_color || '#FFFFFF'}
              onChange={(e) => updateField('text_color', e.target.value)}
            />
          </div>
        </div>
      );

    case 'text':
      return (
        <div className="space-y-4">
          <div>
            <Label>Heading (optional)</Label>
            <Input
              value={content.heading || ''}
              onChange={(e) => updateField('heading', e.target.value)}
            />
          </div>
          <div>
            <Label>Content</Label>
            <Textarea
              value={content.content || ''}
              onChange={(e) => updateField('content', e.target.value)}
              rows={6}
            />
          </div>
          <div>
            <Label>Alignment</Label>
            <Select
              value={content.alignment || 'left'}
              onValueChange={(val) => updateField('alignment', val)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'image':
      return (
        <div className="space-y-4">
          <div>
            <Label>Image</Label>
            <MediaUpload value={content.image_url || ''} purpose="photo"
                         testId="cms-image-block-upload"
                         onChange={(url) => updateField('image_url', url)} />
          </div>
          <div>
            <Label>Alt Text</Label>
            <Input
              value={content.alt_text || ''}
              onChange={(e) => updateField('alt_text', e.target.value)}
            />
          </div>
          <div>
            <Label>Caption (optional)</Label>
            <Input
              value={content.caption || ''}
              onChange={(e) => updateField('caption', e.target.value)}
            />
          </div>
        </div>
      );

    case 'cta':
      return (
        <div className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={content.heading || ''}
              onChange={(e) => updateField('heading', e.target.value)}
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={content.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <Label>Button Text</Label>
            <Input
              value={content.button_text || ''}
              onChange={(e) => updateField('button_text', e.target.value)}
            />
          </div>
          <div>
            <Label>Button Link</Label>
            <Input
              value={content.button_link || ''}
              onChange={(e) => updateField('button_link', e.target.value)}
            />
          </div>
          <div>
            <Label>Background Color</Label>
            <Input
              type="color"
              value={content.background_color || '#F3F4F6'}
              onChange={(e) => updateField('background_color', e.target.value)}
            />
          </div>
        </div>
      );

    case 'contact_info':
      return (
        <div className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input
              value={content.email || ''}
              onChange={(e) => updateField('email', e.target.value)}
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={content.phone || ''}
              onChange={(e) => updateField('phone', e.target.value)}
            />
          </div>
          <div>
            <Label>Address</Label>
            <Textarea
              value={content.address || ''}
              onChange={(e) => updateField('address', e.target.value)}
              rows={2}
            />
          </div>
        </div>
      );

    case 'churches_list':
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Show Global Churches Only</Label>
            <Switch
              checked={content.show_global_only ?? true}
              onCheckedChange={(val) => updateField('show_global_only', val)}
            />
          </div>
          <div>
            <Label>Layout</Label>
            <Select
              value={content.layout || 'grid'}
              onValueChange={(val) => updateField('layout', val)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grid">Grid</SelectItem>
                <SelectItem value="list">List</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Columns</Label>
            <Select
              value={String(content.columns || 3)}
              onValueChange={(val) => updateField('columns', parseInt(val))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 Columns</SelectItem>
                <SelectItem value="3">3 Columns</SelectItem>
                <SelectItem value="4">4 Columns</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Show Logo</Label>
              <Switch
                checked={content.show_logo ?? true}
                onCheckedChange={(val) => updateField('show_logo', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show Location</Label>
              <Switch
                checked={content.show_location ?? true}
                onCheckedChange={(val) => updateField('show_location', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show Contact</Label>
              <Switch
                checked={content.show_contact ?? true}
                onCheckedChange={(val) => updateField('show_contact', val)}
              />
            </div>
          </div>
        </div>
      );

    case 'divider':
      return (
        <div className="space-y-4">
          <div>
            <Label>Style</Label>
            <Select
              value={content.style || 'line'}
              onValueChange={(val) => updateField('style', val)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line">Line</SelectItem>
                <SelectItem value="dashed">Dashed</SelectItem>
                <SelectItem value="dotted">Dotted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Color</Label>
            <Input
              type="color"
              value={content.color || '#E5E7EB'}
              onChange={(e) => updateField('color', e.target.value)}
            />
          </div>
        </div>
      );

    case 'spacer':
      return (
        <div className="space-y-4">
          <div>
            <Label>Height</Label>
            <Select
              value={content.height || 'md'}
              onValueChange={(val) => updateField('height', val)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Small (1rem)</SelectItem>
                <SelectItem value="md">Medium (2rem)</SelectItem>
                <SelectItem value="lg">Large (4rem)</SelectItem>
                <SelectItem value="xl">Extra Large (6rem)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    default:
      return (
        <div className="text-center py-6 text-slate-500">
          <p>Editor for "{block.type}" block type</p>
          <p className="text-sm mt-1">Use the JSON tab to edit raw block data, or pick a supported block type from the toolbar.</p>
        </div>
      );
  }
};

export default CMSPagesList;
